/** PhotobookPanel — crowd favorites (top hearted posts) + keepsake PDF export for the couple. */
import { useEffect, useState, useCallback } from "react";
import api, { formatErr, fileUrl } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Heart, BookHeart, Download } from "lucide-react";

export default function PhotobookPanel() {
  const { eventId } = useMotif();
  const [feed, setFeed] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/feed");
      setFeed(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "feed_new" || msg.type === "feed_update") refresh();
  });

  const favorites = feed
    .filter((p) => (p.likes || []).length > 0)
    .sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0))
    .slice(0, 3);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/photobook/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Guest-Photobook.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Photobook downloaded — enjoy the memories!");
    } catch (e) { toast.error(formatErr(e)); } finally { setDownloading(false); }
  };

  return (
    <div className="card-elev p-5" data-testid="photobook-panel">
      <div className="flex items-center gap-2 mb-3">
        <BookHeart className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-xl">Guest Photobook</div>
      </div>

      {favorites.length > 0 && (
        <>
          <div className="text-[10px] tracking-luxe text-motif-muted mb-2">CROWD FAVORITES</div>
          <div className="space-y-2 mb-4">
            {favorites.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-2 rounded-xl bg-accent-motif" data-testid={`photobook-fav-${p.id}`}>
                {p.image_url && (
                  <img src={fileUrl(p.image_url)} alt="" className="w-12 h-12 object-cover rounded-lg flex-shrink-0" loading="lazy" />
                )}
                <div className="min-w-0 flex-1">
                  {p.caption && <div className="text-xs text-motif truncate">{p.caption}</div>}
                  <div className="text-[10px] text-motif-muted">{p.author_name}</div>
                </div>
                <div className="flex items-center gap-1 text-xs flex-shrink-0" style={{ color: "#c2483f" }}>
                  <Heart className="w-3.5 h-3.5" fill="#c2483f" /> {p.likes.length}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="text-[11px] text-motif-muted mb-3 leading-snug">
        Every guest photo, caption and wish from tonight, bound into a keepsake PDF album.
      </p>
      <button
        onClick={download}
        disabled={downloading}
        className="btn-primary w-full text-sm h-10 flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="photobook-download"
      >
        <Download className="w-4 h-4" /> {downloading ? "Binding your album…" : "Download Photobook (PDF)"}
      </button>
    </div>
  );
}
